import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PurchaseManagerService } from 'src/app/services/purchase-manager/purchase-manager.service';
import { PurchaseManagerModalService } from 'src/app/services/purchase-manager-modal/purchase-manager-modal.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import { GeneralService } from 'src/app/services/general/general.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-purchase-manager',
  templateUrl: './add-edit-purchase-manager.component.html',
  styleUrls: ['./add-edit-purchase-manager.component.scss']
})
export class AddEditPurchaseManagerComponent extends DestructorComponent implements OnInit {

  @Output() purchaseManagerAdded = new EventEmitter<void>();
  @Output() purchaseManagerUpdated = new EventEmitter<void>();

  purchaseManagerForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedPurchaseManager: any = null;
  isVehiclePreSelected: boolean = false;

  // Vehicle search properties
  vehicleSearchTerm: string = '';
  vehicleSearchResults: any[] = [];
  selectedVehicle: any = null;
  showVehicleDropdown: boolean = false;
  isVehicleSearching: boolean = false;
  private vehicleSearchSubject = new Subject<string>();

  // Vendor search properties
  vendorSearchTerm: string = '';
  vendorSearchResults: any[] = [];
  selectedVendor: any = null;
  showVendorDropdown: boolean = false;
  isVendorSearching: boolean = false;
  private vendorSearchSubject = new Subject<string>();

  // File upload properties
  selectedFile: File | null = null;
  uploadedFileUrl: string = '';
  isFileUploading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private _purchaseManagerService: PurchaseManagerService,
    private _purchaseManagerModalService: PurchaseManagerModalService,
    private _vehicleService: VehicleService,
    private _vendorService: VendorService,
    private _generalService: GeneralService
  ) {
    super();
    this.initializeForm();
    this.setupVehicleSearchDebounce();
    this.setupVendorSearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.purchaseManagerForm = this.fb.group({
      vehicle_id: ['', [Validators.required]],
      vehicle_name: [''],
      amount: ['', [Validators.required, Validators.min(0)]],
      purchase_date: ['', [Validators.required]],
      location: ['', [Validators.required]],
      vendor_id: ['', [Validators.required]],
      vendor_name: [''],
      admin_comment: [''],
      invoice_file: ['']
    });
  }

  private getExistingData(vehicle_id: string) {

    this.subscriptions.push(
      this._purchaseManagerService.GetData('1', '', { vehicle_id, paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data && response.data.length) {
            this.selectedPurchaseManager = response.data[0];
            this.populateForm(this.selectedPurchaseManager);
            this.isEditMode = true;
          }
          else {
            this.purchaseManagerForm.patchValue({
              vehicle_id: this.selectedVehicle._id,
              vehicle_name: this.vehicleSearchTerm
            });
          }
        }
      })
    );

  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_purchase_manager_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const servicePurchaseManager = this._purchaseManagerModalService.getSelectedPurchaseManager();
        const preSelectedVehicle = this._purchaseManagerModalService.getPreSelectedVehicle();

        if (servicePurchaseManager) {
          this.isEditMode = true;
          this.selectedPurchaseManager = servicePurchaseManager;
          this.populateForm(this.selectedPurchaseManager);
        } else if (preSelectedVehicle) {
          this.isEditMode = false;
          this.isVehiclePreSelected = true;
          this.selectedVehicle = preSelectedVehicle;
          this.vehicleSearchTerm = this.getVehicleDisplayName(preSelectedVehicle);
          this.getExistingData(preSelectedVehicle._id);

        } else {
          this.isEditMode = false;
          this.isVehiclePreSelected = false;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
        this._purchaseManagerModalService.clearSelectedPurchaseManager();
        this._purchaseManagerModalService.clearPreSelectedVehicle();
      });
    }
  }

  private populateForm(purchaseManager: any): void {
    this.purchaseManagerForm.patchValue({
      vehicle_id: purchaseManager.vehicle_id || '',
      vehicle_name: purchaseManager.vehicle?.name || purchaseManager.vehicle_name || '',
      amount: purchaseManager.amount || '',
      purchase_date: purchaseManager.purchase_date || '',
      location: purchaseManager.location || '',
      vendor_id: purchaseManager.vendor_id || '',
      vendor_name: purchaseManager.vendor?.company_name || purchaseManager.vendor_name || '',
      admin_comment: purchaseManager.admin_comment || '',
      invoice_file: purchaseManager.invoice_file || ''
    });

    // Set vehicle data for display
    if (purchaseManager.vehicle) {
      this.selectedVehicle = purchaseManager.vehicle;
      this.vehicleSearchTerm = this.getVehicleDisplayName(purchaseManager.vehicle);
    }

    // Set vendor data for display
    if (purchaseManager.vendor) {
      this.selectedVendor = purchaseManager.vendor;
      this.vendorSearchTerm = this.getVendorDisplayName(purchaseManager.vendor);
    }

    // Set uploaded file URL
    if (purchaseManager.invoice_file) {
      this.uploadedFileUrl = purchaseManager.invoice_file;
    }
  }

  private resetForm(): void {
    this.purchaseManagerForm.reset();
    this.isEditMode = false;
    this.selectedPurchaseManager = null;
    this.isVehiclePreSelected = false;

    // Reset vehicle search
    this.vehicleSearchTerm = '';
    this.vehicleSearchResults = [];
    this.selectedVehicle = null;
    this.showVehicleDropdown = false;

    // Reset vendor search
    this.vendorSearchTerm = '';
    this.vendorSearchResults = [];
    this.selectedVendor = null;
    this.showVendorDropdown = false;

    // Reset file upload
    this.selectedFile = null;
    this.uploadedFileUrl = '';
    this.isFileUploading = false;
  }

  // Vehicle search methods
  private setupVehicleSearchDebounce(): void {
    this.vehicleSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchVehicles(searchTerm);
    });
  }

  onVehicleSearchInput(event: Event): void {
    // Don't allow search if in edit mode or vehicle is pre-selected
    if (this.isEditMode || this.isVehiclePreSelected) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.vehicleSearchTerm = target.value;
    this.selectedVehicle = null;

    // Update the form control with the typed value
    this.purchaseManagerForm.patchValue({
      vehicle_name: this.vehicleSearchTerm,
      vehicle_id: ''
    });

    if (this.vehicleSearchTerm.length >= 2) {
      this.vehicleSearchSubject.next(this.vehicleSearchTerm);
      this.showVehicleDropdown = true;
    } else {
      this.showVehicleDropdown = false;
      this.vehicleSearchResults = [];
    }
  }

  onVehicleInputFocus(): void {
    // Don't show dropdown if in edit mode or vehicle is pre-selected
    if (this.isEditMode || this.isVehiclePreSelected) {
      return;
    }

    if (this.vehicleSearchResults.length > 0) {
      this.showVehicleDropdown = true;
    }
  }

  onVehicleInputBlur(): void {
    setTimeout(() => {
      this.showVehicleDropdown = false;
    }, 200);
  }

  private searchVehicles(searchTerm: string): void {
    // Don't search if in edit mode or vehicle is pre-selected
    if (this.isEditMode || this.isVehiclePreSelected) {
      return;
    }

    if (!searchTerm || searchTerm.length < 2) {
      this.vehicleSearchResults = [];
      this.showVehicleDropdown = false;
      return;
    }

    this.isVehicleSearching = true;
    this.subscriptions.push(
      this._vehicleService.GetData('1', searchTerm, { paginate: false, search: searchTerm }).subscribe({
        next: (response: any) => {
          this.isVehicleSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.vehicleSearchResults = response.data.data || response.data || [];
            this.showVehicleDropdown = this.vehicleSearchResults.length > 0;
          } else {
            this.vehicleSearchResults = [];
            this.showVehicleDropdown = false;
          }
        },
        error: (error: any) => {
          this.isVehicleSearching = false;
          console.error('Error searching vehicles:', error);
          this.vehicleSearchResults = [];
          this.showVehicleDropdown = false;
        }
      })
    );
  }

  selectVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);

    this.purchaseManagerForm.patchValue({
      vehicle_id: vehicle._id || vehicle.id,
      vehicle_name: this.vehicleSearchTerm
    });

    this.showVehicleDropdown = false;
  }

  public getVehicleDisplayName(vehicle: any): string {
    const parts = [];
    if (vehicle.name) parts.push(vehicle.name);
    if (vehicle.company_name) parts.push(vehicle.company_name);
    if (vehicle.year) parts.push(`(${vehicle.year})`);
    return parts.join(' ') || 'Unknown Vehicle';
  }

  public getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg'; // Default placeholder image
  }

  // Vendor search methods
  private setupVendorSearchDebounce(): void {
    this.vendorSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchVendors(searchTerm);
    });
  }

  onVendorSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.vendorSearchTerm = target.value;
    this.selectedVendor = null;

    // Update the form control with the typed value
    this.purchaseManagerForm.patchValue({
      vendor_name: this.vendorSearchTerm,
      vendor_id: ''
    });

    if (this.vendorSearchTerm.length >= 2) {
      this.vendorSearchSubject.next(this.vendorSearchTerm);
      this.showVendorDropdown = true;
    } else {
      this.showVendorDropdown = false;
      this.vendorSearchResults = [];
    }
  }

  onVendorInputFocus(): void {
    if (this.vendorSearchResults.length > 0) {
      this.showVendorDropdown = true;
    }
  }

  onVendorInputBlur(): void {
    setTimeout(() => {
      this.showVendorDropdown = false;
    }, 200);
  }

  private searchVendors(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.vendorSearchResults = [];
      this.showVendorDropdown = false;
      return;
    }

    this.isVendorSearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false }).subscribe({
        next: (response: any) => {
          this.isVendorSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.vendorSearchResults = response.data.data || response.data || [];
            this.showVendorDropdown = this.vendorSearchResults.length > 0;
          } else {
            this.vendorSearchResults = [];
            this.showVendorDropdown = false;
          }
        },
        error: (error: any) => {
          this.isVendorSearching = false;
          console.error('Error searching vendors:', error);
          this.vendorSearchResults = [];
          this.showVendorDropdown = false;
        }
      })
    );
  }

  selectVendor(vendor: any): void {
    this.selectedVendor = vendor;
    this.vendorSearchTerm = this.getVendorDisplayName(vendor);

    this.purchaseManagerForm.patchValue({
      vendor_id: vendor._id || vendor.id,
      vendor_name: this.vendorSearchTerm
    });

    this.showVendorDropdown = false;
  }

  public getVendorDisplayName(vendor: any): string {
    return vendor.company_name || vendor.name || 'Unknown Vendor';
  }

  // File upload methods
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.selectedFile = file;
      this.uploadFile(file);
    }
  }

  private uploadFile(file: File): void {
    this.isFileUploading = true;
    this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
      next: (res) => {
        const fileUrl = res.data.url;
        const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

        this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
          next: () => {
            this.isFileUploading = false;
            this.uploadedFileUrl = fileUrl;
            this.purchaseManagerForm.patchValue({ invoice_file: fileUrl });
          },
          error: (error) => {
            this.isFileUploading = false;
            console.error('Error uploading file to S3:', error);
            Swal.fire({
              text: "Error uploading file. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        });
      },
      error: (error) => {
        this.isFileUploading = false;
        console.error('Error getting presigned URL:', error);
        Swal.fire({
          text: "Error uploading file. Please try again.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    });
  }

  removeFile(): void {
    this.selectedFile = null;
    this.uploadedFileUrl = '';
    this.purchaseManagerForm.patchValue({ invoice_file: '' });

    // Reset file input
    const fileInput = document.getElementById('invoice_file') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  openFile(): void {
    if (this.uploadedFileUrl) {
      window.open(this.uploadedFileUrl, '_blank');
    }
  }

  // Form submission methods
  onSubmit(): void {
    if (this.purchaseManagerForm.valid) {
      this.isLoading = true;
      const formData = { ...this.purchaseManagerForm.value };

      // Remove display fields that shouldn't be sent to API
      delete formData.vehicle_name;
      delete formData.vendor_name;

      if (this.isEditMode && this.selectedPurchaseManager) {
        this.updatePurchaseManager(formData);
      } else {
        this.addPurchaseManager(formData);
      }
    } else {
      this.markFormGroupTouched(this.purchaseManagerForm);
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields correctly.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }

  private addPurchaseManager(formData: any): void {
    this.subscriptions.push(
      this._purchaseManagerService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Purchase manager record has been added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.purchaseManagerAdded.emit();
              this.resetForm();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add purchase manager record.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error adding purchase manager:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to add purchase manager record. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updatePurchaseManager(formData: any): void {
    const purchaseManagerId = this.selectedPurchaseManager._id || this.selectedPurchaseManager.id;
    this.subscriptions.push(
      this._purchaseManagerService.UpdateData(purchaseManagerId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Purchase manager record has been updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.purchaseManagerUpdated.emit();
              this.resetForm();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update purchase manager record.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error updating purchase manager:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to update purchase manager record. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private markFormGroupTouched(formGroup: any): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control?.controls) {
        this.markFormGroupTouched(control);
      }
    });
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_purchase_manager_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Form validation getters
  get vehicle_id() { return this.purchaseManagerForm.get('vehicle_id'); }
  get amount() { return this.purchaseManagerForm.get('amount'); }
  get purchase_date() { return this.purchaseManagerForm.get('purchase_date'); }
  get location() { return this.purchaseManagerForm.get('location'); }
  get vendor_id() { return this.purchaseManagerForm.get('vendor_id'); }
}
